import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function HousekeepingInfoPage() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [expandedSection, setExpandedSection] = useState(null);

  const sections = [
    {
      id: 'voice',
      title: '🔊 Voice & Audio',
      features: [
        'Every lesson can be read aloud by an AI presenter with professional voice synthesis',
        'Choose between multiple voice styles during your course',
        'Mute audio anytime — text remains fully readable',
        'Adjust playback speed to fit your pace',
      ],
    },
    {
      id: 'transcript',
      title: '📄 Session Transcripts',
      features: [
        'After each training session, you can purchase a PDF transcript for $10',
        'Includes everything: lessons, chat exchanges, exercises, your answers',
        'Builds a personal library of your learning journey',
        'Share transcripts with colleagues or mentors',
      ],
    },
    {
      id: 'resume',
      title: '⏸ Save & Resume',
      features: [
        'Start a lesson, then leave anytime — your progress is automatically saved',
        'Return to any course at any time and pick up exactly where you left off',
        'No time limits — move at your own pace',
        'Your session stays active across all your devices',
      ],
    },
    {
      id: 'interaction',
      title: '💬 How Lessons Work',
      features: [
        'Lessons include interactive AI coaching — ask questions, request rephrasing, demand clarity',
        'Prompt Sandbox: hands-on exercises where you practice directing AI while the instructor watches',
        'Real-time feedback: the instructor sees your work and offers targeted coaching',
        'No "fill in the blank" multiple choice — your real work gets graded fairly',
      ],
    },
    {
      id: 'rules',
      title: '⚖️ Course Rules & Conduct',
      features: [
        'This is a training platform, not a chat service — logged data serves as a record of your learning',
        'Respectful, professional interaction with the instructor. Abusive language will end your session',
        'One account per person. Account sharing is prohibited',
        'Your work and progress are private — visible only to you and your instructor',
      ],
    },
    {
      id: 'certification',
      title: '🏆 Certificates & Progress',
      features: [
        'Foundations tier: completion certificate included',
        'Operator tier: pass all module tests to unlock certificate',
        'Certified tier: final 15-question exam + shareable credential + LinkedIn-ready badge',
        'Progress always visible in your dashboard',
      ],
    },
  ];

  function toggleSection(id) {
    setExpandedSection(expandedSection === id ? null : id);
  }

  return (
    <div style={styles.page}>
      <div style={styles.content}>
        
        <div style={styles.header}>
          <button 
            onClick={() => navigate(-1)}
            style={styles.backBtn}
          >
            ← Back
          </button>
          
          <h1 style={styles.mainHeading}>Here's What to Expect</h1>
          
          <p style={styles.intro}>
            Your course is fully interactive, personalized, and built around <em>your</em> workstyle. Here's what that means.
          </p>
        </div>

        {/* Features Sections */}
        <div style={styles.sectionsContainer}>
          {sections.map((section) => (
            <div key={section.id} style={styles.section}>
              <button
                onClick={() => toggleSection(section.id)}
                style={styles.sectionHeader}
              >
                <span style={styles.sectionTitle}>{section.title}</span>
                <span style={styles.toggleIcon}>
                  {expandedSection === section.id ? '▼' : '▶'}
                </span>
              </button>
              
              {expandedSection === section.id && (
                <div style={styles.sectionContent}>
                  <ul style={styles.featureList}>
                    {section.features.map((feature, idx) => (
                      <li key={idx} style={styles.featureItem}>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Important Info Box */}
        <div style={styles.infoBox}>
          <h3 style={styles.infoTitle}>⏰ Time Commitment</h3>
          <p style={styles.infoText}>
            Most students complete each lesson in 30-45 minutes. There's no rush. Work when it fits your schedule. Your progression is saved automatically.
          </p>
        </div>

        <div style={styles.infoBox}>
          <h3 style={styles.infoTitle}>🎯 Your Learning Path</h3>
          <p style={styles.infoText}>
            Your assessment determined your Operator readiness. Full Operator is the course &mdash; if Foundations was recommended, it means the assessment found gaps in the exact skills Operator builds on. Close those gaps first, and Operator will land the way it’s designed to.
          </p>
        </div>

        {/* CTA Section */}
        <div style={styles.ctaSection}>
          <h2 style={styles.ctaHeading}>Ready to Begin?</h2>
          <p style={styles.ctaText}>
            The assessment recommended a starting point based on your Operator readiness. If Foundations came up, that’s your fastest path to Operator — not a detour.
          </p>
          
          <button 
            onClick={() => navigate('/v2-course')}
            style={styles.primaryBtn}
          >
            Enter the Course →
          </button>
        </div>

      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0a0e1a',
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: '#e2e8f0',
    padding: '40px 24px 80px',
  },
  content: {
    maxWidth: '760px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '48px',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#4f8ef7',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '20px',
    padding: 0,
  },
  mainHeading: {
    fontSize: '36px',
    fontWeight: 800,
    lineHeight: 1.2,
    marginBottom: '16px',
    background: 'linear-gradient(135deg, #f0f4ff 30%, #4f8ef7)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  intro: {
    fontSize: '16px',
    color: '#cbd5e1',
    lineHeight: 1.6,
  },
  sectionsContainer: {
    marginBottom: '48px',
  },
  section: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '10px',
    marginBottom: '12px',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
  },
  sectionHeader: {
    background: 'none',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    padding: '18px 22px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    transition: 'background 0.2s ease',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#f0f4ff',
  },
  toggleIcon: {
    color: '#4f8ef7',
    fontSize: '12px',
  },
  sectionContent: {
    padding: '0 22px 18px 22px',
    borderTop: '1px solid rgba(255,255,255,0.05)',
  },
  featureList: {
    margin: 0,
    paddingLeft: '20px',
    fontSize: '14px',
    lineHeight: 1.8,
    color: '#cbd5e1',
  },
  featureItem: {
    marginBottom: '10px',
  },
  infoBox: {
    background: 'rgba(79,142,247,0.08)',
    border: '1px solid rgba(79,142,247,0.2)',
    borderRadius: '10px',
    padding: '20px 24px',
    marginBottom: '16px',
  },
  infoTitle: {
    fontSize: '15px',
    fontWeight: 700,
    color: '#4f8ef7',
    marginBottom: '8px',
    marginTop: 0,
  },
  infoText: {
    fontSize: '14px',
    color: '#cbd5e1',
    lineHeight: 1.6,
    margin: 0,
  },
  ctaSection: {
    background: 'linear-gradient(135deg, rgba(79,142,247,0.1), rgba(118,75,162,0.05))',
    border: '1px solid rgba(79,142,247,0.15)',
    borderRadius: '12px',
    padding: '32px 28px',
    textAlign: 'center',
    marginBottom: '48px',
  },
  ctaHeading: {
    fontSize: '24px',
    fontWeight: 700,
    marginBottom: '12px',
    color: '#f0f4ff',
  },
  ctaText: {
    fontSize: '15px',
    color: '#cbd5e1',
    lineHeight: 1.6,
    marginBottom: '20px',
  },
  primaryBtn: {
    background: 'linear-gradient(135deg, #4f8ef7, #2563eb)',
    border: 'none',
    color: '#fafafa',
    padding: '12px 32px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  footer: {
    textAlign: 'center',
    paddingTop: '28px',
    borderTop: '1px solid rgba(255,255,255,0.05)',
  },
  footerNote: {
    fontSize: '13px',
    color: '#64748b',
    margin: 0,
  },
};
